import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { canUseOrders } from "../../../helpers/orderEnabled";
import { readAuthUser } from "../../../helpers/studioModules";
import {
  addParcelWithoutMemo,
  getParcel,
  listParcelBoard,
  type ParcelBoard,
  type ParcelBoardView,
  type ParcelDetail,
  type ParcelView,
} from "../../../services/studioSalesApi";
import {
  DeliveredSheet,
  ParcelFormSheet,
  RemoveSheet,
  canShareNatively,
  canUseParcels,
  msg,
  offline,
  useParcels,
  type MemoParcels,
} from "./ParcelsCard";
import SalesSheet from "./SalesSheet";
import {
  BOARD_TABS,
  STAGES,
  checkLine,
  dayLabel,
  daysAgoText,
  deliveredButtonDue,
  eventText,
  eventWho,
  forLine,
  overdueText,
  shareText,
  stageSourceText,
  stageText,
} from "./parcelModel";
import "./salesPhone.scss";

/** How many parcels one view shows: the server's page cap. Search narrows the rest. */
export const BOARD_LIMIT = 50;
const SEARCH_WAIT_MS = 300;

/** iPad and up show a table; a phone shows cards. */
const useTable = () => {
  const query = "(min-width: 768px)";
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [table, setTable] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const list = window.matchMedia(query);
    const on = () => setTable(list.matches);
    list.addEventListener?.("change", on);
    return () => list.removeEventListener?.("change", on);
  }, []);
  return table;
};

const isDone = (p: ParcelView) => p.stage === "DELIVERED" || p.stage === "RETURNED";

/** "Courier check · today" or "Marked by Asha"; what the status rests on. */
const statusNote = (p: ParcelView): { text: string; failed: boolean } | null => {
  const check = checkLine(p);
  if (check?.failed) return check;
  const by = stageSourceText(p);
  if (by) return { text: by, failed: false };
  return check ? { text: check.text.replace(/^checked/, "Checked"), failed: false } : null;
};

/**
 * Every parcel of the studio in one place: on a memo or not, by In transit / Overdue / Delivered / All with counts,
 * searchable, with each parcel's status, last courier check and full history. Cards on a phone, a table from iPad up.
 */
const SalesParcelsPage = () => {
  const history = useHistory();
  const role: string | undefined = readAuthUser()?.users?.role;
  const allowed = canUseParcels(role);
  const table = useTable();
  const [view, setView] = useState<ParcelBoardView>("ACTIVE");
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [counts, setCounts] = useState<ParcelBoard["counts"] | null>(null);
  const [open, setOpen] = useState<ParcelView | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!canUseOrders(role)) history.replace("/dashboard");
  }, [history, role]);

  useEffect(() => {
    const wait = window.setTimeout(() => setQ(typed.trim()), SEARCH_WAIT_MS);
    return () => window.clearTimeout(wait);
  }, [typed]);

  const parcels = useParcels({
    key: `board|${view}|${q}`,
    mode: allowed ? "edit" : null,
    list: async () => {
      const res = await listParcelBoard(view, q, 0, BOARD_LIMIT);
      if (res?.success && res.data) setCounts(res.data.counts);
      return { ...res, data: res?.data ? res.data.items : null, total: res?.data?.total };
    },
    add: (input) => addParcelWithoutMemo(input),
  });

  // A change can move a parcel between views: read the page and the counts again after every save.
  const afterSave = useCallback(() => parcels.reload(), [parcels]);
  const list = parcels.parcels;

  const back = () => (history.length > 1 ? history.goBack() : history.push("/sales"));

  return (
    <div className="sp is-hub pb">
      <div className="sp-head pb-head">
        <button type="button" className="pb-back" aria-label="Back" onClick={back}>‹</button>
        <h1 className="t21 grow pb-title">Parcels</h1>
        {allowed && parcels.status !== "off" ? (
          <button type="button" className="pb-add" onClick={() => setAdding(true)}>+ Track a parcel</button>
        ) : null}
      </div>

      {!allowed ? (
        <p className="t13" role="status">Only the studio owner or a manager can track parcels.</p>
      ) : parcels.status === "off" ? (
        <p className="t13" role="status">Parcel tracking isn't switched on for your studio yet.</p>
      ) : (
        <>
          <input className="sp-search pb-search" type="search" value={typed} maxLength={40}
            placeholder="AWB, memo, invoice or DC no., party, courier" aria-label="Search parcels"
            onChange={(e) => setTyped(e.target.value)} />
          <div className="sp-seg pb-tabs" role="tablist" aria-label="Parcels by status">
            {BOARD_TABS.map((tab) => (
              <button key={tab.view} type="button" role="tab" aria-selected={view === tab.view}
                className={`${view === tab.view ? "is-on" : ""}${tab.view === "OVERDUE" && (counts?.OVERDUE || 0) > 0 ? " is-late" : ""}`}
                onClick={() => setView(tab.view)}>
                {tab.label}{counts ? <span className="pb-count"> {counts[tab.view]}</span> : null}
              </button>
            ))}
          </div>
          <p className="pc-sr" aria-live="polite">{parcels.announce}</p>

          {parcels.status === "loading" ? (
            <p className="t13" role="status">Loading parcels…</p>
          ) : parcels.status === "error" ? (
            <div className="sp-warn" role="alert">
              Couldn't load parcels. <button type="button" className="sp-link pc-inline" onClick={parcels.reload}>Try again</button>
            </div>
          ) : list.length === 0 ? (
            <div className="pc-empty pb-empty">
              <p className="t13">{q ? `No parcel matches “${q}”.`
                : view === "OVERDUE" ? "Nothing late. Every parcel reached in time or is still within its days."
                : view === "DELIVERED" ? "No delivered parcel yet."
                : "No parcel on the way. Add the AWB on a memo, or track one sent without a memo."}</p>
            </div>
          ) : table ? (
            <ParcelTable items={list} onOpen={setOpen} />
          ) : (
            <ul className="pb-cards">{list.map((p) => <ParcelCard key={p.id} p={p} onOpen={setOpen} />)}</ul>
          )}
          {parcels.status === "ready" && parcels.total > list.length ? (
            <p className="t13">Showing the latest {list.length} of {parcels.total}. Search to find an older one.</p>
          ) : null}
        </>
      )}

      {open ? (
        <ParcelSheet parcel={list.find((p) => p.id === open.id) || open} parcels={parcels} onSaved={afterSave}
          onClose={() => setOpen(null)} />
      ) : null}
      {adding ? <ParcelFormSheet parcels={withReload(parcels, afterSave)} withoutMemo onClose={() => setAdding(false)} /> : null}
    </div>
  );
};

/** The same parcel actions, then a fresh read so views and counts follow the change. */
const withReload = (parcels: MemoParcels, after: () => void): MemoParcels => ({
  ...parcels,
  add: async (input, slip) => { const done = await parcels.add(input, slip); if (done.ok) after(); return done; },
  edit: async (p, input) => { const done = await parcels.edit(p, input); if (done.ok) after(); return done; },
  markDelivered: async (p, date, note) => { const done = await parcels.markDelivered(p, date, note); if (done.ok) after(); return done; },
  remove: async (p, reason) => { const done = await parcels.remove(p, reason); if (done.ok) after(); return done; },
});

const StageChip = ({ p }: { p: ParcelView }) => {
  const info = STAGES[p.stage] || STAGES.BOOKED;
  return <span className={`sp-st is-${info.tone} pc-stage`}><span aria-hidden="true">{info.glyph} </span>{stageText(p)}</span>;
};

/** Phone: one card per parcel; the whole card opens it. */
const ParcelCard = ({ p, onOpen }: { p: ParcelView; onOpen: (p: ParcelView) => void }) => {
  const note = statusNote(p);
  const late = overdueText(p);
  return (
    <li>
      <button type="button" className={`pb-card${p.overdue ? " is-late" : ""}`} onClick={() => onOpen(p)}
        aria-label={`${p.courierName} AWB ${p.awbNumber}, ${stageText(p)}`}>
        <span className="pb-card__top">
          <StageChip p={p} />
          <span className="t13 pb-when">Shipped {dayLabel(p.bookedOn)}</span>
        </span>
        <span className="pb-card__awb"><span className="t16">{p.courierName}</span> <span className="mono">{p.awbNumber}</span></span>
        <span className="t13 pb-for">{forLine(p)}</span>
        {p.courierStatusText && !isDone(p) ? <span className="t13 pb-courier-says">Courier: {p.courierStatusText}</span> : null}
        {note ? <span className={`t13 pb-note${note.failed ? " is-bad" : ""}`}>{note.text}</span> : null}
        {late ? <span className="pc-late pb-late"><span aria-hidden="true">! </span>{late}</span> : null}
      </button>
    </li>
  );
};

/** iPad and desktop: one row per parcel; the row opens it. */
const ParcelTable = ({ items, onOpen }: { items: ParcelView[]; onOpen: (p: ParcelView) => void }) => (
  <div className="pb-table-wrap">
    <table className="pb-table">
      <thead>
        <tr>
          <th scope="col">Status</th>
          <th scope="col">Courier · AWB</th>
          <th scope="col">For</th>
          <th scope="col">Shipped</th>
          <th scope="col">Last check</th>
        </tr>
      </thead>
      <tbody>
        {items.map((p) => {
          const note = statusNote(p);
          return (
            <tr key={p.id} className={p.overdue ? "is-late" : ""} tabIndex={0} onClick={() => onOpen(p)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); } }}
              aria-label={`Open ${p.courierName} AWB ${p.awbNumber}`}>
              <td>
                <StageChip p={p} />
                {p.overdue ? <span className="t13 pb-late-tag">Late</span> : null}
              </td>
              <td><span className="t16">{p.courierName}</span><br /><span className="mono pb-awb">{p.awbNumber}</span></td>
              <td className="pb-for">{forLine(p)}</td>
              <td className="pb-nowrap">{dayLabel(p.bookedOn)}</td>
              <td>
                {p.courierStatusText && !isDone(p) ? <span className="pb-courier-says">{p.courierStatusText}</span> : null}
                {note ? <span className={`t13 pb-note${note.failed ? " is-bad" : ""}`}>{note.text}</span>
                  : !p.autoCheck && !isDone(p) ? <span className="t13 pb-note">No automatic check</span> : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

type Sub = "edit" | "delivered" | "remove" | null;

/** One parcel: where it is now, what the courier last said, the actions, and its whole history (every check). */
const ParcelSheet = ({ parcel, parcels, onSaved, onClose }: {
  parcel: ParcelView; parcels: MemoParcels; onSaved: () => void; onClose: () => void;
}) => {
  const history = useHistory();
  const [detail, setDetail] = useState<ParcelDetail | null>(null);
  const [error, setError] = useState("");
  const [sub, setSub] = useState<Sub>(null);
  const [note, setNote] = useState<{ ok: boolean; message: string } | null>(null);
  const [copied, setCopied] = useState("");
  const studio: string = readAuthUser()?.users?.organizationResponse?.name || "";
  const done = isDone(parcel);
  const late = overdueText(parcel);
  const statusLine = statusNote(parcel);
  const writes = useMemo(() => withReload(parcels, onSaved), [parcels, onSaved]);

  const load = useCallback(() => {
    let live = true;
    getParcel(parcel.id)
      .then((res) => {
        if (!live) return;
        if (res?.success && res.data) setDetail(res.data);
        else setError(msg(res, "Couldn't load the history"));
      })
      .catch(() => live && setError(offline));
    return () => { live = false; };
  }, [parcel.id]);
  // Read the history again whenever the parcel changes (a check, a mark, an edit).
  useEffect(() => load(), [load, parcel.version, parcel.lastCheckedAt]);

  const checkNow = async () => {
    const r = await parcels.checkNow(parcel);
    setNote(r);
    if (r.ok) onSaved();
  };
  const share = async () => {
    const text = shareText([parcel], parcel.docNumber || parcel.memoCode || "", studio);
    if (canShareNatively()) {
      try { await navigator.share({ text }); return; } catch (e: any) { if (e?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied("Message copied. Paste it in WhatsApp.");
    } catch {
      setCopied(text);
    }
  };

  if (sub === "edit") return <ParcelFormSheet parcels={writes} parcel={parcel} withoutMemo={!parcel.memoId} onClose={() => setSub(null)} />;
  if (sub === "delivered") return <DeliveredSheet parcels={writes} parcel={parcel} onClose={() => setSub(null)} />;
  if (sub === "remove") return <RemoveSheet parcels={writes} parcel={parcel} onClose={() => { setSub(null); onClose(); }} />;

  const events = detail?.events ? [...detail.events].reverse() : [];
  return (
    <SalesSheet label={`Parcel ${parcel.awbNumber}`} onClose={onClose}>
      <div className="pb-sheet">
        <h2 className="pb-sheet__title">{parcel.courierName} · <span className="mono">{parcel.awbNumber}</span></h2>
        <p className="t13 pb-for">{forLine(parcel)} · shipped {dayLabel(parcel.bookedOn)}</p>

        <div className="pb-now">
          <StageChip p={parcel} />
          {parcel.courierStatusText && !done ? <span className="t16">Courier says: {parcel.courierStatusText}</span> : null}
          {statusLine ? <span className={`t13 pb-note${statusLine.failed ? " is-bad" : ""}`}>{statusLine.text}</span> : null}
          {!parcel.autoCheck && !done ? <span className="t13 pb-note">This courier isn't checked automatically. Open the courier page to see where it is.</span> : null}
          {late ? <span className="pc-late"><span aria-hidden="true">! </span>{late}</span> : null}
        </div>
        {note ? <p className={`t13 pc-note${note.ok ? "" : " pc-note--bad"}`} role="status">{note.message}</p> : null}
        {copied ? <p className="t13 pc-note" role="status">{copied}</p> : null}

        <div className="pb-actions">
          {!done && deliveredButtonDue(parcel) ? (
            <button type="button" className="sp-btn is-pri" onClick={() => setSub("delivered")}>Mark delivered</button>
          ) : null}
          {parcel.autoCheck && !done ? (
            <button type="button" className="sp-btn" disabled={parcels.busy} onClick={() => void checkNow()}>
              {parcels.busy ? "Checking…" : "Check now"}
            </button>
          ) : null}
          {parcel.trackingUrl ? (
            <a className="sp-btn" href={parcel.trackingUrl} target="_blank" rel="noopener noreferrer">Open courier page</a>
          ) : null}
          <button type="button" className="sp-btn" onClick={() => void share()}>Share</button>
        </div>
        <div className="pb-more">
          {parcel.memoId ? (
            <button type="button" className="sp-link" onClick={() => history.push(`/sales/memos/${parcel.memoId}`)}>Open memo</button>
          ) : null}
          {!done && !deliveredButtonDue(parcel) ? (
            <button type="button" className="sp-link" onClick={() => setSub("delivered")}>Mark delivered</button>
          ) : null}
          {parcel.stage === "BOOKED" ? <button type="button" className="sp-link" onClick={() => setSub("edit")}>Edit</button> : null}
          <button type="button" className="sp-link is-danger" onClick={() => setSub("remove")}>Remove</button>
        </div>

        {detail?.slipPhotoUrl ? (
          <img className="pc-slip-photo" src={detail.slipPhotoUrl} loading="lazy" alt={`Courier slip for AWB ${parcel.awbNumber}`} />
        ) : null}

        <h3 className="t16 pb-hist-title">History</h3>
        {error ? <div className="sp-warn" role="alert">{error}</div> : null}
        {!detail && !error ? <p className="t13" role="status">Loading…</p> : null}
        {detail && events.length === 0 ? <p className="t13">Nothing yet.</p> : null}
        {events.length ? (
          <ol className="pb-history" aria-label="History, newest first">
            {events.map((e, i) => (
              <li key={i} className={`pb-step is-${e.kind.toLowerCase()}`}>
                <span className="t13 pb-step__when">
                  {new Date(e.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{" "}
                  {new Date(e.at).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}
                </span>
                <span className="t13"><b>{eventWho(e)}</b>: {eventText(e)}</span>
              </li>
            ))}
          </ol>
        ) : null}
        {parcel.lastCheckedAt ? <p className="t13 pb-note">Last courier check {daysAgoText(parcel.lastCheckedAt)}.</p> : null}
        <button type="button" className="sp-btn" onClick={onClose}>Close</button>
      </div>
    </SalesSheet>
  );
};

export default SalesParcelsPage;
